// Panel de Configuración → Categorías. CRUD del catálogo dinámico de
// categorías de caso (antes hardcodeadas en NuevoCasoModal y en el viejo lib/api.ts).
// El `slug` se deriva del nombre al crear y NO se edita (es lo que se guarda
// en el caso). Renombrar solo cambia la etiqueta visible.
import { type FormEvent, useEffect, useState } from "react";
import { ApiError, api, type Categoria } from "@/lib/api";
import ConfirmDialog from "@/ui/ConfirmDialog";
import { SkeletonRows } from "@/ui/Skeleton";
import { useToast } from "@/ui/Toast";

export default function CategoriasPanel() {
  const toast = useToast();
  const [cats, setCats] = useState<Categoria[]>([]);
  const [loading, setLoading] = useState(true);
  const [nombreNuevo, setNombreNuevo] = useState("");
  const [creando, setCreando] = useState(false);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [editNombre, setEditNombre] = useState("");
  const [confirmDel, setConfirmDel] = useState<Categoria | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const list = await api.get<Categoria[]>(
        "/api/categorias-caso?incluir_inactivos=true",
      );
      setCats(list);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function crear(e: FormEvent) {
    e.preventDefault();
    if (!nombreNuevo.trim()) return;
    setCreando(true);
    try {
      const c = await api.post<Categoria>("/api/categorias-caso", {
        nombre: nombreNuevo.trim(),
      });
      toast.success("Categoría creada", c.nombre);
      setNombreNuevo("");
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo crear", msg);
    } finally {
      setCreando(false);
    }
  }

  async function toggleActivo(c: Categoria) {
    try {
      await api.patch(`/api/categorias-caso/${c.id}`, { activo: !c.activo });
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo actualizar", msg);
    }
  }

  function startEdit(c: Categoria) {
    setEditandoId(c.id);
    setEditNombre(c.nombre);
  }

  async function saveEdit(c: Categoria) {
    if (!editNombre.trim() || editNombre === c.nombre) {
      setEditandoId(null);
      return;
    }
    try {
      await api.patch(`/api/categorias-caso/${c.id}`, {
        nombre: editNombre.trim(),
      });
      toast.success("Categoría renombrada");
      setEditandoId(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo renombrar", msg);
    }
  }

  async function mover(c: Categoria, dir: -1 | 1) {
    const idx = cats.findIndex((x) => x.id === c.id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= cats.length) return;
    const otra = cats[swapIdx];
    try {
      await api.patch(`/api/categorias-caso/${c.id}`, { orden: otra.orden });
      await api.patch(`/api/categorias-caso/${otra.id}`, { orden: c.orden });
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo reordenar", msg);
    }
  }

  async function eliminar(c: Categoria) {
    try {
      await api.del(`/api/categorias-caso/${c.id}`);
      toast.success("Categoría eliminada", c.nombre);
      await refresh();
    } catch (err) {
      // El backend devuelve 409 si la categoría tiene casos asociados.
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo eliminar", msg);
    }
  }

  return (
    <>
      <form
        onSubmit={crear}
        className="card flex flex-col gap-3 sm:flex-row sm:items-end"
      >
        <div className="flex-1">
          <label className="text-xs uppercase tracking-wide text-zinc-400">
            Nueva categoría
          </label>
          <input
            type="text"
            className="input mt-1"
            value={nombreNuevo}
            onChange={(e) => setNombreNuevo(e.target.value)}
            placeholder="ej. Garantía, Devolución, Visita técnica"
          />
        </div>
        <button
          type="submit"
          disabled={!nombreNuevo.trim() || creando}
          className="btn-primary disabled:opacity-50"
        >
          {creando ? "Creando..." : "+ Agregar"}
        </button>
      </form>

      <div className="card">
        <div className="text-xs text-zinc-500 mb-3">
          Estas categorías alimentan el selector de "Nuevo caso" y las
          estadísticas. El identificador interno (
          <span className="font-mono">slug</span>) se fija al crear y no
          cambia al renombrar. No se puede eliminar una categoría con casos:
          desactívala para ocultarla sin perder el histórico.
        </div>
        {loading ? (
          <SkeletonRows rows={6} cols={2} />
        ) : cats.length === 0 ? (
          <p className="text-sm text-zinc-500">
            No hay categorías todavía. Agrega la primera arriba.
          </p>
        ) : (
          <ul className="divide-y divide-ink-700">
            {cats.map((c, i) => (
              <li
                key={c.id}
                className={`py-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between ${c.activo ? "" : "opacity-50"}`}
              >
                {editandoId === c.id ? (
                  <input
                    type="text"
                    autoFocus
                    className="input flex-1"
                    value={editNombre}
                    onChange={(e) => setEditNombre(e.target.value)}
                    onBlur={() => saveEdit(c)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEdit(c);
                      if (e.key === "Escape") setEditandoId(null);
                    }}
                  />
                ) : (
                  <span className="flex-1 flex items-baseline gap-2">
                    <span className="text-zinc-200">{c.nombre}</span>
                    <span className="text-[11px] font-mono text-zinc-500">
                      {c.slug}
                    </span>
                  </span>
                )}
                <div className="flex flex-wrap gap-1 sm:flex-shrink-0">
                  <button
                    onClick={() => mover(c, -1)}
                    aria-label={`Subir ${c.nombre}`}
                    disabled={i === 0}
                    className="btn-ghost text-xs disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => mover(c, 1)}
                    aria-label={`Bajar ${c.nombre}`}
                    disabled={i === cats.length - 1}
                    className="btn-ghost text-xs disabled:opacity-30"
                  >
                    ↓
                  </button>
                  <button
                    onClick={() => toggleActivo(c)}
                    className="btn-ghost text-xs"
                  >
                    {c.activo ? "Desactivar" : "Activar"}
                  </button>
                  <button
                    onClick={() => startEdit(c)}
                    className="btn-ghost text-xs"
                  >
                    Renombrar
                  </button>
                  <button
                    onClick={() => setConfirmDel(c)}
                    className="btn-ghost text-xs text-red-400 hover:text-red-300"
                  >
                    Eliminar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {confirmDel && (
        <ConfirmDialog
          open={true}
          onOpenChange={(v) => !v && setConfirmDel(null)}
          title={`Eliminar "${confirmDel.nombre}"`}
          description="Si la categoría tiene casos asociados no se podrá eliminar; en ese caso desactívala. Los casos viejos conservan su categoría."
          variant="danger"
          confirmLabel="Eliminar"
          onConfirm={() => eliminar(confirmDel)}
        />
      )}
    </>
  );
}
